/** 与 `.lib-grid`（minmax(160px, 1fr) + gap 14px）及 compact 卡片高度估算一致 */

export const LIBRARY_GRID_MIN_COL_PX = 160;
export const LIBRARY_GRID_GAP_PX = 14;
/** 预览 120px + 信息区 + 行间距 */
export const LIBRARY_GRID_ROW_HEIGHT_PX = 196;

export const LIBRARY_PORTRAIT_PAGE_SIZE_MIN = 8;
export const LIBRARY_PORTRAIT_PAGE_SIZE_MAX = 120;
export const LIBRARY_PORTRAIT_MIN_VISIBLE_ROWS = 2;
/** 首屏多预取半行，减少初次即触发加载 */
export const LIBRARY_PORTRAIT_INITIAL_EXTRA_ROWS = 1;
/** 下拉加载时每次追加的行数 */
export const LIBRARY_PORTRAIT_LOAD_MORE_ROWS = 2;

/** 右侧素材库抽屉：固定 2 列（见 libraryDrawer.css） */
export const LIBRARY_DRAWER_GRID_COLS = 2;
export const LIBRARY_DRAWER_GRID_GAP_PX = 8;
/** mini 卡片 80px 预览 + 信息区 + 网格 gap */
export const LIBRARY_DRAWER_ROW_HEIGHT_PX = 124;
export const LIBRARY_DRAWER_HEADER_PX = 56;
export const LIBRARY_DRAWER_HINT_PX = 40;
export const LIBRARY_DRAWER_BODY_PAD_PX = 24;

/** 素材选择器弹窗：`.lp-grid` minmax(140px, 1fr) + gap 12px */
export const LIBRARY_PICKER_GRID_MIN_COL_PX = 140;
export const LIBRARY_PICKER_GRID_GAP_PX = 12;
export const LIBRARY_PICKER_ROW_HEIGHT_PX = 124;
export const LIBRARY_PICKER_HEADER_PX = 52;
export const LIBRARY_PICKER_HINT_PX = 28;
export const LIBRARY_PICKER_BODY_PAD_PX = 32;

export function computeLibraryGridColumnCount(containerWidth: number): number {
  const w = Math.max(containerWidth, LIBRARY_GRID_MIN_COL_PX);
  return Math.max(
    1,
    Math.floor((w + LIBRARY_GRID_GAP_PX) / (LIBRARY_GRID_MIN_COL_PX + LIBRARY_GRID_GAP_PX)),
  );
}

export function computeLibraryPortraitRowsForViewport(
  viewportHeight: number,
  reservedTopPx: number,
): number {
  const available = Math.max(LIBRARY_GRID_ROW_HEIGHT_PX, viewportHeight - reservedTopPx);
  return Math.max(
    LIBRARY_PORTRAIT_MIN_VISIBLE_ROWS,
    Math.ceil(available / LIBRARY_GRID_ROW_HEIGHT_PX) + LIBRARY_PORTRAIT_INITIAL_EXTRA_ROWS,
  );
}

/** 首屏请求条数 = 列数 × 可见行数 */
export function computeLibraryPortraitInitialPageSize(options: {
  viewportHeight: number;
  containerWidth: number;
  reservedTopPx?: number;
}): number {
  const cols = computeLibraryGridColumnCount(options.containerWidth);
  const rows = computeLibraryPortraitRowsForViewport(
    options.viewportHeight,
    options.reservedTopPx ?? 168,
  );
  const size = cols * rows;
  return Math.min(
    LIBRARY_PORTRAIT_PAGE_SIZE_MAX,
    Math.max(LIBRARY_PORTRAIT_PAGE_SIZE_MIN, size),
  );
}

/** 下拉加载每页条数（与首屏列宽对齐，按固定行数追加） */
export function computeLibraryPortraitLoadMorePageSize(containerWidth: number): number {
  const cols = computeLibraryGridColumnCount(containerWidth);
  return Math.min(
    LIBRARY_PORTRAIT_PAGE_SIZE_MAX,
    Math.max(LIBRARY_PORTRAIT_PAGE_SIZE_MIN, cols * LIBRARY_PORTRAIT_LOAD_MORE_ROWS),
  );
}

function computePortraitRowsForViewport(
  viewportHeight: number,
  reservedTopPx: number,
  rowHeightPx: number,
): number {
  const available = Math.max(rowHeightPx, viewportHeight - reservedTopPx);
  return Math.max(
    LIBRARY_PORTRAIT_MIN_VISIBLE_ROWS,
    Math.ceil(available / rowHeightPx) + LIBRARY_PORTRAIT_INITIAL_EXTRA_ROWS,
  );
}

/** 右侧抽屉首屏条数 = 2 列 × 可见行数 */
export function computeLibraryDrawerPortraitInitialPageSize(options?: {
  viewportHeight?: number;
  reservedTopPx?: number;
}): number {
  const viewportHeight = options?.viewportHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 800);
  const reservedTopPx =
    options?.reservedTopPx ??
    LIBRARY_DRAWER_HEADER_PX + LIBRARY_DRAWER_HINT_PX + LIBRARY_DRAWER_BODY_PAD_PX;
  const rows = computePortraitRowsForViewport(viewportHeight, reservedTopPx, LIBRARY_DRAWER_ROW_HEIGHT_PX);
  const size = LIBRARY_DRAWER_GRID_COLS * rows;
  return Math.min(
    LIBRARY_PORTRAIT_PAGE_SIZE_MAX,
    Math.max(LIBRARY_PORTRAIT_PAGE_SIZE_MIN, size),
  );
}

/** 抽屉下拉加载每页条数 */
export function computeLibraryDrawerPortraitLoadMorePageSize(): number {
  return Math.min(
    LIBRARY_PORTRAIT_PAGE_SIZE_MAX,
    Math.max(
      LIBRARY_PORTRAIT_PAGE_SIZE_MIN,
      LIBRARY_DRAWER_GRID_COLS * LIBRARY_PORTRAIT_LOAD_MORE_ROWS,
    ),
  );
}

export function computeLibraryPickerColumnCount(containerWidth: number): number {
  const w = Math.max(containerWidth, LIBRARY_PICKER_GRID_MIN_COL_PX);
  return Math.max(
    1,
    Math.floor(
      (w + LIBRARY_PICKER_GRID_GAP_PX) /
        (LIBRARY_PICKER_GRID_MIN_COL_PX + LIBRARY_PICKER_GRID_GAP_PX),
    ),
  );
}

/** 素材选择器首屏条数 = 列数 × 可见行数 */
export function computeLibraryPickerPortraitInitialPageSize(options?: {
  containerWidth?: number;
  bodyHeight?: number;
}): number {
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const containerWidth =
    options?.containerWidth ??
    (typeof window !== 'undefined' ? Math.min(568, window.innerWidth * 0.9 - 32) : 568);
  const bodyHeight =
    options?.bodyHeight ?? viewportHeight * 0.7 - LIBRARY_PICKER_HEADER_PX - LIBRARY_PICKER_HINT_PX;
  const cols = computeLibraryPickerColumnCount(containerWidth);
  const rows = Math.max(
    LIBRARY_PORTRAIT_MIN_VISIBLE_ROWS,
    Math.ceil(Math.max(bodyHeight, LIBRARY_PICKER_ROW_HEIGHT_PX) / LIBRARY_PICKER_ROW_HEIGHT_PX) +
      LIBRARY_PORTRAIT_INITIAL_EXTRA_ROWS,
  );
  const size = cols * rows;
  return Math.min(
    LIBRARY_PORTRAIT_PAGE_SIZE_MAX,
    Math.max(LIBRARY_PORTRAIT_PAGE_SIZE_MIN, size),
  );
}

/** 选择器下拉加载每页条数 */
export function computeLibraryPickerPortraitLoadMorePageSize(containerWidth: number): number {
  const cols = computeLibraryPickerColumnCount(containerWidth);
  return Math.min(
    LIBRARY_PORTRAIT_PAGE_SIZE_MAX,
    Math.max(LIBRARY_PORTRAIT_PAGE_SIZE_MIN, cols * LIBRARY_PORTRAIT_LOAD_MORE_ROWS),
  );
}
